import {
  Box,
  Button,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import PageTitle from "@components/PageTitle";

import ESignViewer from "@containers/ESign/ESignViewer";

import { getRecordId } from "@utils/helpers";

import AuthService from "../../services/authService";
import UserService from "../../services/userService";
import { LStorage, extractError } from "../../services/utils";

type Document = {
  _id?: string;
  documentId: string;
  signatureTimestamp: string;
  signedDocument: string;
  documentName: string;
  status: "Signed" | "Incomplete";
};

const ConsentFormsPage = () => {
  const patientId: string = AuthService.getAuth()?.user?.id;
  const [documents, setDocuments] = useState<Document[]>([]);
  const [signingDetails, setSigningDetails] = useState<Partial<Document>>();
  const [loading, setLoading] = useState(true);
  const isMobile = LStorage.getItem("SOURCE_HOST") === "mobile";

  useEffect(() => {
    getDocuments();
  }, [patientId]);

  const getDocuments = () => {
    if (!loading) setLoading(true);
    UserService.getSignatureDocuments({ patientId })
      .then((res) => {
        const data: Document[] = res.data || [];
        setDocuments(data);
      })
      .catch(() => toast.error("We are unable to get the documents"))
      .finally(() => setLoading(false));
  };

  const updateDownloadAudit = (document: Document) => {
    const id = getRecordId(document);
    if (id)
      UserService.updateSignatureAudit({
        id,
        patientId,
        documentId: document.documentId,
        auditEvent: "SIGNED_DOCUMENT_DOWNLOADED",
      }).catch(() => {});
  };

  const emailSignedDocument = (document: Document) => {
    const id = getRecordId(document);
    if (id)
      UserService.emailSignatureDocument({
        id,
        patientId,
        documentId: document.documentId,
      })
        .then(() =>
          toast.success(
            "We successfully emailed you a copy of your signed document.",
          ),
        )
        .catch((e) =>
          extractError(e, "Unable to send copy of signed document"),
        );
  };

  if (signingDetails?.documentId)
    return (
      <ESignViewer
        showERSD="once"
        title="Documents: 1 of 1"
        documentDetails={signingDetails}
        onClose={() => setSigningDetails({})}
        onSigned={() => {
          setSigningDetails({ documentId: "" });
          getDocuments();
        }}
      />
    );

  if (loading) return null;
  return (
    <Box className="page-wrap">
      <PageTitle title="Consent Forms" containerSx={{ padding: 1 }} />
      <Container>
        {documents.length ? (
          <List>
            {documents.map((document) => (
              <ListItem
                key={document.documentId}
                sx={{
                  mb: 1,
                  padding: 1,
                  gap: 1,
                  background: "white",
                  borderRadius: "8px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <ListItemText
                  sx={{ flexBasis: "70%" }}
                  primary={document.documentName}
                  secondary={
                    <>
                      <Typography variant="body2">
                        Status: {document.status}
                      </Typography>
                      {document.signatureTimestamp && (
                        <Typography variant="body2">
                          Date:{" "}
                          {dayjs(document.signatureTimestamp).format("LLL")}
                        </Typography>
                      )}
                    </>
                  }
                />
                {document.status === "Signed" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ maxWidth: 120 }}
                    onClick={() =>
                      isMobile
                        ? emailSignedDocument(document)
                        : updateDownloadAudit(document)
                    }
                    href={isMobile ? undefined : document.signedDocument}
                  >
                    {isMobile ? "Email" : "Download"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ maxWidth: 120 }}
                    onClick={() => setSigningDetails(document)}
                  >
                    Sign
                  </Button>
                )}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box
            textAlign="center"
            sx={{
              background: "white",
              padding: 2,
              borderRadius: "8px",
              marginTop: 10,
              marginRight: "auto",
              marginLeft: "auto",
              width: "90%",
            }}
          >
            <Typography variant="h6">
              No signatures required or submitted at this moment
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ConsentFormsPage;
